import { useEffect, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { core } from "../../lib/api";
import { CustomFieldInput } from "./custom-field-input";
import type { CampaignField } from "./types";

type PublicForm = { name: string; fields: CampaignField[] };

const inputCls = "mt-2 w-full rounded-lg border border-line bg-transparent px-3 py-2.5 text-ink";

export function PublicCrmFormPage() {
  const { token } = useParams();
  const [form, setForm] = useState<PublicForm | null>(null);
  const [error, setError] = useState("");
  const [name, setName] = useState(""), [phone, setPhone] = useState(""), [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false), [done, setDone] = useState(false);
  useEffect(() => {
    setForm(null); setError(""); setDone(false); setValues({}); setName(""); setPhone("");
    if (!token) { setError("Form not found."); return; }
    void core<PublicForm>(`/api/v1/public/crm/forms/${encodeURIComponent(token)}`)
      .then(setForm)
      .catch(e => setError(e instanceof Error ? e.message : "This form is no longer available."));
  }, [token]);
  // Full name / Phone number are just entries in `form.fields` now (a
  // campaign can relabel, make either optional, or remove it), always
  // returned first by the backend when present — everything else is a
  // custom question.
  const nameField = form?.fields.find(f => f.id === "name");
  const phoneField = form?.fields.find(f => f.id === "phone");
  const customFields = form?.fields.filter(f => f.id !== "name" && f.id !== "phone") ?? [];
  const submit = async (event: FormEvent) => {
    event.preventDefault(); if (busy) return;
    setBusy(true); setError("");
    try {
      if (!form || !token) throw new Error("Form not found.");
      if (nameField?.required && !name.trim()) throw new Error(`${nameField.label} is required.`);
      if (phoneField?.required && !phone.trim()) throw new Error(`${phoneField.label} is required.`);
      if (customFields.some(field => field.required && !values[field.id]?.trim())) throw new Error("Please answer all required questions.");
      await core(`/api/v1/public/crm/forms/${encodeURIComponent(token)}/submissions`, { method: "POST", body: JSON.stringify({ name: name.trim() || undefined, phone: phone.trim() || undefined, values }) });
      setDone(true);
    } catch (e) { setError(e instanceof Error ? e.message : "Please try again."); }
    finally { setBusy(false); }
  };
  return <main className="min-h-screen bg-soft px-4 py-12 text-ink"><div className="mx-auto max-w-xl rounded-2xl border border-line bg-elevated p-6 shadow-sm sm:p-8">
    <p className="mb-5 text-sm font-bold text-brand">Techladder.ai</p><p className="mb-5 rounded-lg bg-soft p-3 text-xs text-ink-2">Your response is sent securely to the team that shared this form.</p>
    {done ? <div role="status"><h1 className="text-sm font-semibold text-ink">Thank you!</h1><p className="mt-3 text-ink-2">Your response has been submitted.</p></div> : <>
      <h1 className="text-sm font-semibold text-ink">{form?.name || "Customer form"}</h1>
      {!form && !error && <p className="mt-4">Loading form…</p>}
      {form && (
        <form onSubmit={submit} className="mt-6">
          {nameField && <label className="block border-b border-line pb-5 text-sm font-semibold">{nameField.label}{nameField.required ? " *" : ""}<input required={nameField.required} maxLength={200} autoComplete="name" value={name} onChange={e => setName(e.target.value)} className={inputCls} /></label>}
          {phoneField && <label className="block border-b border-line py-5 text-sm font-semibold">{phoneField.label}{phoneField.required ? " *" : ""}<input required={phoneField.required} type="tel" maxLength={30} autoComplete="tel" value={phone} onChange={e => setPhone(e.target.value)} className={inputCls} /></label>}
          {customFields.map(field => (
            <div key={field.id} className="border-b border-line py-5 text-sm font-semibold">
              <p className="mb-2">{field.label}{field.required ? " *" : ""}</p>
              <CustomFieldInput field={field} value={values[field.id] || ""} onChange={v => setValues(prev => ({ ...prev, [field.id]: v }))} inputClassName={inputCls + " mt-0"} />
            </div>
          ))}
          <button disabled={busy} className="w-full rounded-lg bg-brand px-4 py-3 font-semibold text-white disabled:opacity-50">{busy ? "Submitting…" : "Submit"}</button>
        </form>
      )}
      {error && <p role="alert" className="mt-4 text-sm text-red-600">{error}</p>}
    </>}
  </div></main>;
}
